import { useMemo, useState } from "react";
import SectionHeading from "@/components/feature/SectionHeading";
import Reveal from "@/components/base/Reveal";
import PlanCards, { type PlanCardItem } from "@/components/feature/PlanCards";
import VpsComparisonTable from "@/pages/vps/components/VpsComparisonTable";
import {
  vpsPlansBrSsd,
  vpsPlansEuaSsd,
  vpsPlansBrHd,
  vpsPlansEuaHd,
} from "@/mocks/vps";

type Region = "all" | "br" | "eua";
type DiskType = "all" | "ssd" | "hd";

interface PlanSubGroup {
  key: string;
  label: string;
  icon: string;
  type: Exclude<DiskType, "all">;
  columns: 3 | 4;
  plans: PlanCardItem[];
}

interface PlanGroup {
  key: string;
  title: string;
  description: string;
  icon: string;
  tone: "primary" | "accent";
  region: Exclude<Region, "all">;
  subGroups: PlanSubGroup[];
}

const GROUPS: PlanGroup[] = [
  {
    key: "br",
    title: "VPS — Brasil",
    description:
      "Datacenter no Rio de Janeiro. Escolha SSD RAID10 de alta performance ou a opção em HD para projetos de menor demanda.",
    icon: "ri-map-pin-2-line",
    tone: "primary",
    region: "br",
    subGroups: [
      {
        key: "br-ssd",
        label: "SSD",
        icon: "ri-flashlight-line",
        type: "ssd",
        columns: 4,
        plans: vpsPlansBrSsd,
      },
      {
        key: "br-hd",
        label: "HD",
        icon: "ri-hard-drive-2-line",
        type: "hd",
        columns: 3,
        plans: vpsPlansBrHd,
      },
    ],
  },
  {
    key: "eua",
    title: "VPS — EUA",
    description:
      "Alto desempenho internacional com links de até 1 Gbit. SSD RAID10 para grande tráfego ou HD como alternativa econômica.",
    icon: "ri-flag-line",
    tone: "accent",
    region: "eua",
    subGroups: [
      {
        key: "eua-ssd",
        label: "SSD",
        icon: "ri-flashlight-line",
        type: "ssd",
        columns: 4,
        plans: vpsPlansEuaSsd,
      },
      {
        key: "eua-hd",
        label: "HD",
        icon: "ri-hard-drive-2-line",
        type: "hd",
        columns: 3,
        plans: vpsPlansEuaHd,
      },
    ],
  },
];

const REGION_OPTIONS: { value: Region; label: string; icon: string }[] = [
  { value: "all", label: "Todas", icon: "ri-earth-line" },
  { value: "br", label: "Brasil", icon: "ri-map-pin-2-line" },
  { value: "eua", label: "EUA", icon: "ri-flag-line" },
];

const TYPE_OPTIONS: { value: DiskType; label: string; icon: string }[] = [
  { value: "all", label: "Todos", icon: "ri-apps-2-line" },
  { value: "ssd", label: "SSD", icon: "ri-flashlight-line" },
  { value: "hd", label: "HD", icon: "ri-hard-drive-2-line" },
];

const TONE_CLASS: Record<string, string> = {
  primary: "bg-primary-100 text-primary-600",
  accent: "bg-accent-100 text-accent-700",
};

const SUBTONE_CLASS: Record<string, string> = {
  ssd: "bg-primary-100 text-primary-700",
  hd: "bg-secondary-100 text-secondary-700",
};

const VPS_CTA_MESSAGE = "Ola! Quero fazer o pedido do plano";

export default function VpsPlansSection() {
  const [region, setRegion] = useState<Region>("all");
  const [type, setType] = useState<DiskType>("all");

  const filteredGroups = useMemo(
    () =>
      GROUPS.filter((group) => region === "all" || group.region === region)
        .map((group) => ({
          ...group,
          subGroups: group.subGroups.filter(
            (subGroup) => type === "all" || subGroup.type === type,
          ),
        }))
        .filter((group) => group.subGroups.length > 0),
    [region, type],
  );

  const visiblePlans = useMemo(
    () => filteredGroups.flatMap((group) => group.subGroups.flatMap((subGroup) => subGroup.plans)),
    [filteredGroups],
  );

  const hasResults = filteredGroups.length > 0;

  return (
    <section id="planos-vps" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          badge="Planos de VPS"
          badgeIcon="ri-price-tag-3-line"
          title="Escolha o VPS ideal para o seu projeto."
          description="Filtre por região e tipo de disco para encontrar o plano certo em segundos. Os recursos podem ser ajustados conforme a necessidade do seu projeto."
        />

        <Reveal>
          <div className="mt-10 flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                <i className="ri-map-pin-2-line" aria-hidden="true"></i>
                Região
              </span>
              <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                {REGION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setRegion(option.value)}
                    aria-pressed={region === option.value}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      region === option.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={option.icon} aria-hidden="true"></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                <i className="ri-hard-drive-2-line" aria-hidden="true"></i>
                Tipo de disco
              </span>
              <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setType(option.value)}
                    aria-pressed={type === option.value}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      type === option.value
                        ? "bg-secondary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={option.icon} aria-hidden="true"></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {hasResults ? (
          <>
            <div className="mt-6 text-sm text-foreground-600">
              {visiblePlans.length} {visiblePlans.length === 1 ? "plano encontrado" : "planos encontrados"}
            </div>

            {filteredGroups.map((group) => (
              <div key={group.key} className="mt-14 first:mt-10">
                <div className="flex flex-col gap-3 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${TONE_CLASS[group.tone]}`}
                    >
                      <i className={`${group.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-foreground-950">
                      {group.title}
                    </h3>
                  </div>
                  <p className="max-w-xl text-sm text-foreground-600">{group.description}</p>
                </div>

                {group.subGroups.map((subGroup) => (
                  <div key={subGroup.key} className="mt-10">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${SUBTONE_CLASS[subGroup.type]}`}
                      >
                        <i className={subGroup.icon} aria-hidden="true"></i>
                        {subGroup.label}
                      </span>
                      <span className="text-sm text-foreground-500">
                        {subGroup.plans.length}{" "}
                        {subGroup.plans.length === 1 ? "plano" : "planos"}
                      </span>
                    </div>

                    <div className="mt-6">
                      <PlanCards
                        plans={subGroup.plans}
                        columns={subGroup.columns}
                        ctaMessage={VPS_CTA_MESSAGE}
                        ctaLabel="Fazer pedido no WhatsApp"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <Reveal>
              <div className="mt-16">
                <h3 className="font-heading text-xl font-bold text-foreground-950">
                  Comparativo lado a lado
                </h3>
                <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                  Veja os recursos de cada plano lado a lado para decidir com segurança. Arraste para
                  o lado para ver todos os planos.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className="mt-6">
                <VpsComparisonTable plans={visiblePlans} />
              </div>
            </Reveal>
          </>
        ) : (
          <Reveal>
            <div className="mt-12 rounded-xl border border-dashed border-background-300 bg-background-50 p-10 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-filter-off-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">
                Nenhum plano de VPS encontrado para essa combinação. Tente outra região ou tipo de
                disco.
              </p>
            </div>
          </Reveal>
        )}

        <Reveal>
          <p className="mt-10 text-center text-xs text-foreground-500">
            Os recursos apresentados são configuráveis e podem ser ajustados conforme a necessidade
            do seu projeto.
          </p>
        </Reveal>
      </div>
    </section>
  );
}